import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CaretDown, X, MagnifyingGlass, ClockClockwise, Users } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// Cached HS-code list (Jamaica ASYCUDA Manifest, 5,205 codes). Loaded once
// from the static asset at /data/jm-hs-codes.json — subsequent picker opens
// reuse the same in-memory list across the app session.
let _hsCache = null;
let _hsLoadPromise = null;
async function loadHsCodes() {
  if (_hsCache) return _hsCache;
  if (!_hsLoadPromise) {
    _hsLoadPromise = fetch("/data/jm-hs-codes.json")
      .then((r) => r.json())
      .then((arr) => {
        _hsCache = arr;
        return arr;
      })
      .catch(() => {
        _hsLoadPromise = null;
        return [];
      });
  }
  return _hsLoadPromise;
}

// Recent HS codes are kept in localStorage per browser-user (~1KB total).
// Most courier admins ship the same 3–4 commodity categories repeatedly, so
// pinning the last 10 picks at the top of the picker eliminates 99% of the
// repeated search-and-scroll loops.
const RECENT_HS_KEY = "kanal.hs.recent";
const RECENT_HS_CAP = 10;

function loadRecentHsCodes() {
  try {
    const raw = localStorage.getItem(RECENT_HS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x) => x && typeof x.c === "string" && x.c.length === 6).slice(0, RECENT_HS_CAP)
      : [];
  } catch {
    return [];
  }
}

function pushRecentHsCode(item) {
  if (!item || !item.c) return;
  try {
    const cur = loadRecentHsCodes();
    const next = [item, ...cur.filter((x) => x.c !== item.c)].slice(0, RECENT_HS_CAP);
    localStorage.setItem(RECENT_HS_KEY, JSON.stringify(next));
  } catch {
    /* localStorage disabled / full — silently no-op */
  }
}

// Company-wide "most used" HS codes — sourced from the backend aggregation
// over the team's package history. Cached per session so we don't hit the
// API every time the picker opens.
let _teamTopCache = null;
let _teamTopPromise = null;
async function loadTeamTopHsCodes() {
  if (_teamTopCache) return _teamTopCache;
  if (!_teamTopPromise) {
    _teamTopPromise = api
      .get("/hs-codes/team-top", { params: { limit: 5 } })
      .then(({ data }) => {
        _teamTopCache = Array.isArray(data) ? data : [];
        return _teamTopCache;
      })
      .catch(() => {
        _teamTopPromise = null;
        return [];
      });
  }
  return _teamTopPromise;
}

/**
 * Searchable Jamaica ASYCUDA HS code picker.
 *
 * Props:
 *  - value: 6-digit code string (or empty)
 *  - onChange: (code: string) => void
 *  - testId: data-testid prefix
 *
 * Matches by code prefix first (so typing "8517" surfaces all phone/comms codes
 * instantly), then by description substring. Results capped at 100 rows for
 * snappy rendering — the search input narrows everything else.
 */
export function HsCodePicker({ value, onChange, testId = "hs-code-picker", placeholder = "Search HS code or description..." }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState(() => loadRecentHsCodes());
  // Company-wide most-used HS codes (top 5). Lazy-fetched on first open, cached
  // for the session. Surfaces team knowledge to brand-new dispatchers.
  const [teamTop, setTeamTop] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && codes.length === 0) {
      setLoading(true);
      loadHsCodes()
        .then((arr) => setCodes(arr || []))
        .finally(() => setLoading(false));
    }
    if (open) {
      // Refresh recents on every open so picks made elsewhere in the app
      // (e.g. on the Package Create form) show up immediately in this picker.
      setRecents(loadRecentHsCodes());
      // Best-effort team-top fetch (cached for the session)
      loadTeamTopHsCodes().then(setTeamTop);
    }
  }, [open, codes.length]);

  // Re-focus the search input when the popover opens
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!codes.length) return [];
    const q = query.trim().toLowerCase();
    if (!q) {
      // Empty query: show the first 100 codes (or the currently-selected one at top)
      const head = codes.slice(0, 100);
      if (value) {
        const sel = codes.find((x) => x.c === value);
        if (sel && !head.includes(sel)) return [sel, ...head.slice(0, 99)];
      }
      return head;
    }
    const isDigits = /^\d+$/.test(q);
    const results = [];
    // First pass — code prefix matches (e.g. "85" → all "85xxxx" codes)
    if (isDigits) {
      for (const item of codes) {
        if (item.c.startsWith(q)) {
          results.push(item);
          if (results.length >= 100) break;
        }
      }
      if (results.length >= 100) return results;
    }
    // Second pass — description substring matches (skip dupes from first pass)
    const seen = new Set(results.map((r) => r.c));
    for (const item of codes) {
      if (seen.has(item.c)) continue;
      if (item.d.toLowerCase().includes(q)) {
        results.push(item);
        if (results.length >= 100) break;
      }
    }
    return results;
  }, [codes, query, value]);

  const selected = useMemo(
    () => codes.find((x) => x.c === value) || (value ? { c: value, d: "" } : null),
    [codes, value],
  );

  const pick = (item) => {
    onChange(item.c);
    pushRecentHsCode(item);
    setRecents(loadRecentHsCodes());
    setOpen(false);
    setQuery("");
  };

  const clearRecents = (e) => {
    e.stopPropagation();
    try { localStorage.removeItem(RECENT_HS_KEY); } catch { /* ignore */ }
    setRecents([]);
  };

  // Join the team-top codes against the loaded catalog so we have descriptions
  // for each. Drop any codes that already appear in personal Recents to avoid
  // visual duplication.
  const teamTopWithDesc = useMemo(() => {
    if (!teamTop.length || !codes.length) return [];
    const recentSet = new Set(recents.map((r) => r.c));
    return teamTop
      .filter((t) => !recentSet.has(t.code))
      .map((t) => {
        const cat = codes.find((x) => x.c === t.code);
        return { c: t.code, d: cat?.d || "", count: t.count };
      })
      .filter((t) => t.d); // skip codes that aren't in our catalog
  }, [teamTop, codes, recents]);

  // Only show the Recents / Team-top sections when there's no active search —
  // once the user types anything, the unified filtered results take over.
  const showPinned = !query.trim() && (recents.length > 0 || teamTopWithDesc.length > 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testId}-trigger`}
          className="mt-1 w-full flex items-center justify-between gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-left hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#002BF6]/20"
        >
          <span className="flex-1 min-w-0">
            {selected ? (
              <>
                <span className="font-mono font-semibold text-zinc-900">{selected.c}</span>
                {selected.d && (
                  <span className="ml-2 text-zinc-600 truncate inline-block max-w-[18rem] align-bottom">
                    {selected.d}
                  </span>
                )}
              </>
            ) : (
              <span className="text-zinc-400">Select an HS code...</span>
            )}
          </span>
          {value && (
            <X
              size={14}
              className="text-zinc-400 hover:text-zinc-700 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              data-testid={`${testId}-clear`}
            />
          )}
          <CaretDown size={12} className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[min(560px,calc(100vw-2rem))] max-h-[70vh] overflow-hidden flex flex-col"
        align="start"
        data-testid={`${testId}-popover`}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 shrink-0">
          <MagnifyingGlass size={14} className="text-zinc-400" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className="border-0 shadow-none focus-visible:ring-0 px-0 h-7"
            data-testid={`${testId}-input`}
          />
        </div>
        <div className="overflow-y-auto" data-testid={`${testId}-list`}>
          {loading ? (
            <div className="px-3 py-8 text-center text-xs text-zinc-500">
              Loading Jamaica ASYCUDA HS codes...
            </div>
          ) : (
            <>
              {showPinned && (
                <div data-testid={`${testId}-pinned`}>
                  {recents.length > 0 && (
                    <div data-testid={`${testId}-recents`}>
                      <div className="px-3 py-1.5 bg-amber-50/60 border-b border-amber-100 flex items-center justify-between gap-2 sticky top-0 z-10">
                        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-700">
                          <ClockClockwise size={11} weight="bold" />
                          Recently picked
                        </span>
                        <button
                          type="button"
                          onClick={clearRecents}
                          className="text-[10px] text-amber-700/70 hover:text-amber-900 hover:underline"
                          data-testid={`${testId}-clear-recents`}
                        >
                          Clear
                        </button>
                      </div>
                      {recents.map((item) => (
                        <button
                          key={`recent-${item.c}`}
                          type="button"
                          onClick={() => pick(item)}
                          data-testid={`${testId}-recent-${item.c}`}
                          className={`w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-zinc-50 flex items-start gap-3 transition-colors ${
                            item.c === value ? "bg-blue-50" : ""
                          }`}
                        >
                          <span className="font-mono text-xs font-semibold text-amber-700 shrink-0 w-14">
                            {item.c}
                          </span>
                          <span className="text-xs text-zinc-700 leading-snug">
                            {item.d}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {teamTopWithDesc.length > 0 && (
                    <div data-testid={`${testId}-team-top`}>
                      <div className="px-3 py-1.5 bg-emerald-50/60 border-b border-emerald-100 flex items-center gap-1.5">
                        <Users size={11} weight="bold" className="text-emerald-700" />
                        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                          Your team&apos;s top codes
                        </span>
                      </div>
                      {teamTopWithDesc.map((item) => (
                        <button
                          key={`team-${item.c}`}
                          type="button"
                          onClick={() => pick(item)}
                          data-testid={`${testId}-team-${item.c}`}
                          className={`w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-zinc-50 flex items-start gap-3 transition-colors ${
                            item.c === value ? "bg-blue-50" : ""
                          }`}
                        >
                          <span className="font-mono text-xs font-semibold text-emerald-700 shrink-0 w-14">
                            {item.c}
                          </span>
                          <span className="text-xs text-zinc-700 leading-snug flex-1">
                            {item.d}
                          </span>
                          <span
                            className="shrink-0 text-[10px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded"
                            title={`Used ${item.count} time${item.count === 1 ? "" : "s"} by your team`}
                          >
                            ×{item.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="px-3 py-1 text-[10px] text-zinc-400 text-center border-b border-zinc-100 bg-white">
                    All codes
                  </div>
                </div>
              )}
              {filtered.length === 0 ? (
                <div className="px-3 py-8 text-center text-xs text-zinc-500" data-testid={`${testId}-empty`}>
                  No HS codes match &ldquo;{query}&rdquo;.
                </div>
              ) : (
                <>
                  {filtered.map((item) => (
                    <button
                      key={item.c}
                      type="button"
                      onClick={() => pick(item)}
                      data-testid={`${testId}-item-${item.c}`}
                      className={`w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-50 last:border-0 flex items-start gap-3 transition-colors ${
                        item.c === value ? "bg-blue-50" : ""
                      }`}
                    >
                      <span className="font-mono text-xs font-semibold text-[#002BF6] shrink-0 w-14">
                        {item.c}
                      </span>
                      <span className="text-xs text-zinc-700 leading-snug">
                        {item.d}
                      </span>
                    </button>
                  ))}
                  {filtered.length === 100 && (
                    <div className="px-3 py-2 text-[11px] text-zinc-400 text-center border-t border-zinc-100">
                      Showing first 100 matches — refine your search to narrow further
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        <div className="px-3 py-2 border-t border-zinc-100 text-[10px] text-zinc-500 shrink-0">
          {codes.length > 0 ? `${codes.length.toLocaleString()} Jamaica ASYCUDA Manifest HS codes` : ""}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Field wrapper combining a label + the HS code picker. Optional `hint` text.
 */
export function HsCodeField({ label, hint, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
        {label}
      </Label>
      <HsCodePicker value={value} onChange={onChange} testId={testId} />
      {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}
